import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { ThreadInfo, FileSearchResult } from './types';
import { ReactStateSetter } from '../../types.ts';

interface ThreadContextProps {
    threadId: string | null;
    setThreadId: ReactStateSetter<string | null>;
    files: FileSearchResult[];
    setFiles: ReactStateSetter<FileSearchResult[]>;
}

export const ThreadContext = createContext<ThreadContextProps>({
    threadId: null,
    setThreadId: () => {},
    files: [],
    setFiles: () => {}
});

interface ThreadProviderProps {
    children: ReactNode;
}

export const ThreadProvider: React.FC<ThreadProviderProps> = ({ children }) => {
    const [threadInfo, setThreadInfo] = useState<ThreadInfo | null>(() => {
        const storedInfo = sessionStorage.getItem('threadInfo');
        return storedInfo ? JSON.parse(storedInfo) : null;
    });

    const threadId = threadInfo?.threadId || null;
    const files = threadInfo?.fileSearchResults || [];

    useEffect(() => {
        if (threadInfo) {
            sessionStorage.setItem('threadInfo', JSON.stringify(threadInfo));
        } else {
            sessionStorage.removeItem('threadInfo');
        }
    }, [threadInfo]);

    const setThreadId: ReactStateSetter<string | null> = (
        newThreadIdOrUpdater
    ) => {
        setThreadInfo((prev) => {
            const newThreadId =
                typeof newThreadIdOrUpdater === 'function'
                    ? newThreadIdOrUpdater(prev?.threadId || null)
                    : newThreadIdOrUpdater;

            if (newThreadId === null) {
                return null;
            }

            return {
                threadId: newThreadId,
                fileSearchResults: prev?.fileSearchResults || []
            };
        });
    };

    const setFiles: ReactStateSetter<FileSearchResult[]> = (
        newFilesOrUpdater
    ) => {
        setThreadInfo((prev) => {
            const newFiles =
                typeof newFilesOrUpdater === 'function'
                    ? newFilesOrUpdater(prev?.fileSearchResults || [])
                    : newFilesOrUpdater;

            if (prev) {
                return {
                    ...prev,
                    fileSearchResults: newFiles
                };
            }

            return null;
        });
    };

    return (
        <ThreadContext.Provider
            value={{
                threadId,
                setThreadId,
                files,
                setFiles
            }}
        >
            {children}
        </ThreadContext.Provider>
    );
};
